import { getRouteByPath } from '@routes/utils/getRouteBy'
import { getRouterMethod } from '@routes/utils/getRouterMethod'
import { toUrlString } from '@routes/utils/isUrlString'
import Route from '@routes/utils/Route'
import { RouterMethod } from '@routes/utils/types'
import { canCheckForContainerApp, isContainerApp } from '@util/device'
import { logError } from '@util/logger'
import { noop } from 'lodash-es'
import {
    ReadonlyURLSearchParams,
    usePathname,
    // This is the only place in the entire repository that we should be importing useRouter!
    // eslint-disable-next-line no-restricted-imports
    useRouter,
    useSearchParams,
} from 'next/navigation'
import { useMemo, useRef } from 'react'

type AppRouterInstance = ReturnType<typeof useRouter>
type UsePathnameReturnType = ReturnType<typeof usePathname>
type UseSearchParamsReturnType = ReturnType<typeof useSearchParams>
type Push = AppRouterInstance['push']
type Replace = AppRouterInstance['replace']
type NavigateOptions = Parameters<Push>[1] & Parameters<Replace>[1]
export type InterNationsAppRouter = AppRouterInstance & {
    pathname: UsePathnameReturnType | null
    searchParams: UseSearchParamsReturnType
    go: (route: TRoute, navigationOptions?: NavigateOptions) => void
}

/**
 * Navigate directly to a route. Only for use with App Router.
 */
export function go({
    fromRoute,
    toRoute,
    push,
    replace,
    navigationOptions,
}: {
    fromRoute: TRoute
    toRoute: TRoute
    push: Push
    replace: Replace
    navigationOptions?: NavigateOptions
}): void {
    if (toRoute.isINPOKEMON3273) {
        logError(`Called .go() on an INPOKEMON3273 route: ${toRoute.id}`)
    }

    if (!toRoute.isReady) {
        logError(`Cannot navigate to route ${toRoute.id} because it has not yet received the required path arguments`)
        return
    }

    const method: RouterMethod = getRouterMethod({
        currentRoute: fromRoute,
        nextRoute: toRoute,

        // These need to be passed to the custom function. If they are imported directly into the definition file, then it causes a circular dependency with Jest.
        isContainerApp: canCheckForContainerApp() && isContainerApp(),
    })

    if (method === 'full-page-load') {
        window.location.href = toRoute.toString()
        return
    }

    const url = toRoute.toString()

    if (method === 'push') {
        return push(url, navigationOptions)
    }

    return replace(url, navigationOptions)
}

/**
 * Creates a `go` function that has baked-in values for NextRouter and QueryClient
 */
function createGo({ fromRoute, push, replace }: { fromRoute: TRoute; push: Push; replace: Replace }) {
    /**
     * Navigate directly to a route
     */
    return function enhancedGo(toRoute: TRoute, navigationOptions?: NavigateOptions): void {
        return go({
            fromRoute,
            toRoute,
            push,
            replace,
            navigationOptions,
        })
    }
}

function useRefRouter(): AppRouterInstance {
    const router = useRouter()
    const ref = useRef(router)

    ref.current = router

    return ref.current
}

const fallbackRouter: InterNationsAppRouter = {
    back: noop,
    forward: noop,
    go: (route) => {
        const logMeta = { metadata: { route } }

        if (!route.isReady) {
            logError('Called go() before the route was ready', logMeta)
        }

        if (process.env.STAGE === 'test') {
            console.warn(
                'You might need to call usePathnameMock.mockReturnValue(...) in order for useINAppRouter to work correctly'
            )
        }

        logError('Called go() before the NextRouter was ready. Will fall back to standard browser navigation.', logMeta)
        window.location.href = route.toString()
    },
    pathname: '',
    searchParams: new ReadonlyURLSearchParams(),
    prefetch: noop,
    push: noop,
    refresh: noop,
    replace: noop,
}

function getRoute(pathname: string | null) {
    const fromRoute = getRouteByPath(pathname)

    // For tests, if `usePathname` is mocked, we can use that to create the current route
    if (!fromRoute && process.env.STAGE === 'test' && pathname) {
        return new Route(toUrlString(pathname))
    }

    return fromRoute
}

export function useINAppRouter(): InterNationsAppRouter {
    const nextRouter = useRefRouter()
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const enhancedRouter: InterNationsAppRouter = useMemo(
        () => {
            const fromRoute = getRoute(pathname)

            // Next's router is not yet ready, so return dummy values instead
            if (!fromRoute) {
                return fallbackRouter
            }

            return {
                ...nextRouter,
                pathname,
                searchParams,
                go: createGo({ fromRoute, ...nextRouter }),
            }
        },
        // For many properties of `nextRouter`, such as `push` and `replace` methods, we don't need "fresh" copies of
        // them. So we only include certain properties of `nextRouter` in the dependency array.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pathname, searchParams?.toString()]
    )

    return enhancedRouter
}
